/**
 * Recommendation add-to-cart tracking.
 *
 * Pushes a `recommendation_add_to_cart` event to the dataLayer (Elevar/GTM -> GA4)
 * when a product is added from a recommendation list that opted in. A list opts in
 * by rendering `data-track-list="<list name>"` on the element that contains its add
 * form - for `horizontal-product`, pass `track_list` and it lands on both the
 * single-variant form and the `<quick-buy-drawer>`.
 *
 * The quick-buy drawer is moved to <body> when opened, so the hook has to live on
 * the drawer itself rather than on a list wrapper.
 *
 * Listens for the theme's `variant:add` event, which ProductForm dispatches (bubbling)
 * after a successful /cart/add.js with the added line items and the refreshed cart.
 * One event is pushed per added item. `quantity` is what was submitted, read from the
 * form, because the response item's quantity is the cart line total.
 *
 * The listener runs in the capture phase: QuickBuyDrawer handles `variant:add` on
 * itself by replacing its children, which detaches the form before the event
 * bubbles back up to document and leaves `closest()` with nothing to find.
 *
 * Event params: item_list_name, product_id, product_title, variant_id, variant_title,
 * price (decimal, presentment/cart currency), quantity, currency.
 *
 * Fire-and-forget: the handler is wrapped so it can never break add to cart.
 */
(() => {
  "use strict";

  // Elevar's app embed creates this, but it loads async and may be blocked by
  // consent - a local array keeps pushes safe and lets GTM drain them later.
  window.dataLayer = window.dataLayer || [];

  document.addEventListener("variant:add", (event) => {
    try {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const source = target.closest("[data-track-list]");
      const listName = source && source.dataset.trackList;
      if (!listName) return;

      const detail = event.detail || {};
      const items = Array.isArray(detail.items) ? detail.items : [];
      const currency = (detail.cart && detail.cart.currency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "";

      const quantityInput = target.elements && target.elements.quantity;
      const quantity = parseInt(quantityInput && quantityInput.value, 10) || 1;

      items.forEach((item) => {
        window.dataLayer.push({
          event: "recommendation_add_to_cart",
          item_list_name: listName,
          product_id: String(item.product_id || ""),
          product_title: item.product_title || "",
          variant_id: String(item.variant_id || ""),
          variant_title: item.variant_title || "",
          price: Number(((item.final_price ?? item.price ?? 0) / 100).toFixed(2)),
          quantity,
          currency,
        });
      });
    } catch (error) {
      console.error("recommendation add-to-cart handler failed.", error);
    }
  }, { capture: true });
})();
